import { useCallback } from 'react';
import type { WizardStep } from '../types/WizardTypes';

interface UseWizardValidationReturn {
  validateStep: (stepId: string, data: any) => Promise<true | string | string[]>;
  validateAllSteps: (allData: Record<string, any>) => Promise<Record<string, true | string | string[]>>;
  clearStepErrors: (stepId: string) => void;
}

/**
 * Hook for wizard step validation
 * Handles validation logic for individual steps and the entire wizard
 */
export function useWizardValidation(steps: WizardStep[]): UseWizardValidationReturn {
  
  const validateStep = useCallback(async (stepId: string, data: any): Promise<true | string | string[]> => {
    const step = steps.find(s => s.id === stepId);
    
    if (!step || !step.validation) {
      return true;
    }

    const { validate, required, allowSkip } = step.validation;

    // Check if step is required and has data
    if (required && (!data || Object.keys(data).length === 0)) {
      return 'This step is required';
    }

    // If step is optional and empty, allow it unless allowSkip is false
    if (!required && (!data || Object.keys(data).length === 0) && allowSkip !== false) {
      return true;
    }

    // Run custom validation if provided
    if (validate) {
      try {
        const result = validate(data);
        return result;
      } catch (error) {
        return error instanceof Error ? error.message : 'Validation error occurred';
      }
    }

    return true;
  }, [steps]);

  const validateAllSteps = useCallback(async (allData: Record<string, any>): Promise<Record<string, true | string | string[]>> => {
    const results: Record<string, true | string | string[]> = {};

    for (const step of steps) {
      const stepData = allData[step.id] || {};
      results[step.id] = await validateStep(step.id, stepData);
    }

    return results;
  }, [steps, validateStep]);

  const clearStepErrors = useCallback((stepId: string) => {
    // This is handled by the parent component's state management
    // Included for API completeness
  }, []);

  return {
    validateStep,
    validateAllSteps,
    clearStepErrors
  };
}